import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { manageAdminApi } from "../../../../entities/admin";
import {
  loadLocalData,
  saveLocalData,
  removeLocalData,
} from "../../../../utils/common-util";
import { KEYS } from "../../../../constants-keys";

interface User {
  id: string;
  username: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  verifyToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const verifyToken = async (): Promise<boolean> => {
    try {
      const token = loadLocalData(KEYS.LOGIN_TOKEN);
      if (!token) {
        return false;
      }

      const result = await manageAdminApi.verifyToken();
      if (result.valid && result.user) {
        setUser(result.user);
        setIsAuthenticated(true);
        return true;
      } else {
        // 토큰이 유효하지 않으면 로컬 스토리지 정리
        removeLocalData(KEYS.LOGIN_TOKEN);
        removeLocalData(KEYS.IS_ADMIN);
        removeLocalData(KEYS.LOGIN_USER);
        setIsAuthenticated(false);
        setUser(null);
        return false;
      }
    } catch (error) {
      console.error("토큰 검증 실패:", error);
      // 에러 발생 시 로컬 스토리지 정리
      removeLocalData(KEYS.LOGIN_TOKEN);
      removeLocalData(KEYS.IS_ADMIN);
      removeLocalData(KEYS.LOGIN_USER);
      setIsAuthenticated(false);
      setUser(null);
      return false;
    }
  };

  const login = async (
    username: string,
    password: string,
  ): Promise<boolean> => {
    try {
      const result = await manageAdminApi.login({ username, password });
      if (result.success && result.token) {
        saveLocalData(KEYS.LOGIN_TOKEN, result.token);
        saveLocalData(KEYS.IS_ADMIN, "true");
        saveLocalData(KEYS.LOGIN_USER, username);

        setUser({
          id: result.user?.id || "",
          username: result.user?.username || username,
        });
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("로그인 실패:", error);
      return false;
    }
  };

  const logout = () => {
    removeLocalData(KEYS.LOGIN_TOKEN);
    removeLocalData(KEYS.IS_ADMIN);
    removeLocalData(KEYS.LOGIN_USER);
    setIsAuthenticated(false);
    setUser(null);
  };

  // 컴포넌트 마운트 시 토큰 검증
  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      await verifyToken();
      setLoading(false);
    };

    initAuth();
  }, []);

  // 주기적으로 토큰 검증 (5분마다)
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(
        () => {
          verifyToken();
        },
        5 * 60 * 1000,
      ); // 5분

      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const value: AuthContextType = {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    verifyToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
